import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea
} from 'recharts';
import { BACKEND_URL } from '../config';
import DateRangeSlider from './Shared/DateRangeSlider';
import { parseLocalDate, formatLocalDate } from '../utils/localDate';

function AdvancedAnalytics({ theme }) {
  const { t, isSpanish } = useLanguage();
  const catName = (cat) => isSpanish ? (cat.name_español || cat.name) : cat.name;

  // Helper function to calculate ISO week number
  const getISOWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };

  // Helper function to get date from ISO week
  const getDateFromISOWeek = (year, week) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const ISOweekStart = simple;
    if (dow <= 4) {
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    } else {
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    }
    return ISOweekStart;
  };

  // Time period state (independent from QuickViewStats)
  const [timePeriod, setTimePeriod] = useState('all');
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [selectedHour, setSelectedHour] = useState(() => new Date().getHours());
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });

  // State
  const [filters, setFilters] = useState({
    granularity: 'week',
    categoryId: '',
    productId: '',
    userId: ''
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [categoryTimeSeriesData, setCategoryTimeSeriesData] = useState(null);
  const [individualCategoryTimeSeriesData, setIndividualCategoryTimeSeriesData] = useState(null);
  const [userTimeSeriesData, setUserTimeSeriesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tips report state. The tips section keeps its own granularity so changing it
  // does not refetch the sales charts, and its own loading and error state so a
  // failed tips query does not blank the rest of the page.
  const [tipsData, setTipsData] = useState(null);
  const [tipsGranularity, setTipsGranularity] = useState('day');
  const [tipsLoading, setTipsLoading] = useState(false);
  const [tipsError, setTipsError] = useState(null);

  // Date range resolved by fetchAnalyticsData, reused by the tips report so the
  // period logic lives in one place
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  // Chart display state
  const [chartMetric, setChartMetric] = useState('revenue'); // 'revenue' or 'orders'
  const [visibleLines, setVisibleLines] = useState({
    total: true,
    byGroup: false,
    byCategory: false,
    byUser: false
  });
  const [hiddenLines, setHiddenLines] = useState(new Set()); // Track individually hidden lines by dataKey

  // Zoom/brush state for drag-to-zoom functionality (refs to avoid full re-renders during drag)
  const refAreaLeftRef = useRef(null);
  const refAreaRightRef = useRef(null);
  const [refAreaTick, setRefAreaTick] = useState(0);

  // Expose zoom helpers for screenshot automation (removed on unmount)

  // Pie chart drill-down state
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);

  // Product drill-down state (third level)
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [productDrillDownData, setProductDrillDownData] = useState(null);

  // Category group configuration state
  const [showGroupConfig, setShowGroupConfig] = useState(false);
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [tempGroups, setTempGroups] = useState(null);

  // Group editing state
  const [editingGroup, setEditingGroup] = useState(null); // { groupKey, name, color, description }
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupFormData, setGroupFormData] = useState({ group_key: '', name: '', color: '#1976d2', description: '' });

  // Dropdown options
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  // Configurable category groups (loaded from database)
  const [categoryGroups, setCategoryGroups] = useState({});

  // Touch device detection
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect touch capability on mount
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    };
    checkTouch();
  }, []);

  // Chart colors - dynamically generated from groups using useMemo to react to changes
  const CHART_COLORS = useMemo(() => ({
    ...Object.keys(categoryGroups).reduce((acc, key) => {
      acc[key] = categoryGroups[key]?.color || '#999';
      return acc;
    }, {}),
    other: theme?.colors?.error || '#f44336',
    line: theme?.colors?.primary || '#1976d2',
    grid: theme?.colors?.border || '#e0e0e0',
    text: theme?.colors?.text || '#000',
    surface: theme?.colors?.surface || '#fff'
  }), [categoryGroups, theme]);

  // Fetch dropdown options and category group assignments
  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchUsers();
    fetchCategoryGroupAssignments();
  }, []);

  // Fetch analytics data when filters or time period changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAnalyticsData();
    }, 500); // Debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, timePeriod, customDateRange, selectedDate, selectedHour]);

  // Fetch individual category and user time series when checkboxes are enabled
  useEffect(() => {
    const fetchAdditionalData = async () => {
      // Calculate date range
      let startDate, endDate;
      const now = new Date();

      if (timePeriod === 'custom') {
        startDate = customDateRange.start;
        endDate = customDateRange.end;
      } else if (timePeriod === 'all') {
        startDate = '2000-01-01';
        endDate = formatLocalDate(now);
      } else if (timePeriod === 'hour' || timePeriod === 'day') {
        startDate = selectedDate;
        endDate = selectedDate;
      } else if (timePeriod === 'week') {
        const selected = parseLocalDate(selectedDate);
        const dayOfWeek = selected.getDay();
        const startOfWeek = new Date(selected);
        startOfWeek.setDate(selected.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
      } else if (timePeriod === 'month') {
        const selected = parseLocalDate(selectedDate);
        const startOfMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
        const endOfMonth = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
        startDate = formatLocalDate(startOfMonth);
        endDate = formatLocalDate(endOfMonth);
      } else if (timePeriod === 'year') {
        const selected = parseLocalDate(selectedDate);
        const startOfYear = new Date(selected.getFullYear(), 0, 1);
        const endOfYear = new Date(selected.getFullYear(), 11, 31);
        startDate = formatLocalDate(startOfYear);
        endDate = formatLocalDate(endOfYear);
      }

      // Fetch individual category time series if enabled
      if (visibleLines.byCategory) {
        await fetchIndividualCategoryTimeSeries(startDate, endDate);
      }

      // Fetch user time series if enabled
      if (visibleLines.byUser) {
        await fetchUserTimeSeries(startDate, endDate);
      }
    };

    const timeoutId = setTimeout(() => {
      if (visibleLines.byCategory || visibleLines.byUser) {
        fetchAdditionalData();
      }
    }, 500); // Debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleLines.byCategory, visibleLines.byUser, filters, timePeriod, selectedDate, selectedHour, customDateRange]);

  // Fetch tips once the date range is known, and again when the tips
  // granularity or the user filter changes
  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;

    const timeoutId = setTimeout(() => {
      fetchTipsData(dateRange.start, dateRange.end);
    }, 500); // Debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, tipsGranularity, filters.userId]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchCategoryGroupAssignments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/category-groups`);
      if (response.ok) {
        const data = await response.json();
        // Data now includes name, color, description from database
        setCategoryGroups(data);
      }
    } catch (err) {
      console.error('Error fetching category group assignments:', err);
    }
  };

  const saveCategoryGroupAssignments = async (groups) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/category-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ groups })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (err) {
      console.error('Error saving category group assignments:', err);
      throw err;
    }
  };

  const createGroup = async (groupData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(groupData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error creating group:', err);
      throw err;
    }
  };

  const updateGroup = async (groupKey, groupData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/groups/${groupKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(groupData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error updating group:', err);
      throw err;
    }
  };

  const deleteGroup = async (groupKey) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/groups/${groupKey}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error deleting group:', err);
      throw err;
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate date range based on timePeriod (same logic as QuickViewStats)
      let startDate, endDate;
      const now = new Date();

      if (timePeriod === 'custom') {
        // Custom date range from zoom selection
        startDate = customDateRange.start || formatLocalDate(now);
        endDate = customDateRange.end || formatLocalDate(now);
      } else if (timePeriod === 'all') {
        // All time - use very old start date
        startDate = '2000-01-01';
        endDate = formatLocalDate(now);
      } else if (timePeriod === 'hour') {
        // Specific hour on selectedDate
        startDate = selectedDate;
        endDate = selectedDate;
      } else if (timePeriod === 'day') {
        // Specific day
        startDate = selectedDate;
        endDate = selectedDate;
      } else if (timePeriod === 'week') {
        // Week containing selectedDate
        const selected = parseLocalDate(selectedDate);
        const dayOfWeek = selected.getDay();
        const startOfWeek = new Date(selected);
        startOfWeek.setDate(selected.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
      } else if (timePeriod === 'month') {
        // Month containing selectedDate
        const selected = parseLocalDate(selectedDate);
        const startOfMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
        const endOfMonth = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
        startDate = formatLocalDate(startOfMonth);
        endDate = formatLocalDate(endOfMonth);
      } else if (timePeriod === 'year') {
        // Year containing selectedDate
        const selected = parseLocalDate(selectedDate);
        startDate = `${selected.getFullYear()}-01-01`;
        endDate = `${selected.getFullYear()}-12-31`;
      } else {
        // Default to current day
        startDate = formatLocalDate(now);
        endDate = formatLocalDate(now);
      }

      // Hand the resolved range to the tips report without recomputing it there.
      // Keep the previous object when the range is unchanged so a category or
      // product filter change does not refetch tips, which ignores those filters.
      setDateRange(prev => (
        prev.start === startDate && prev.end === endDate ? prev : { start: startDate, end: endDate }
      ));

      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        granularity: filters.granularity
      });

      if (filters.categoryId) queryParams.append('categoryId', filters.categoryId);
      if (filters.productId) queryParams.append('productId', filters.productId);
      if (filters.userId) queryParams.append('userId', filters.userId);
      if (timePeriod === 'hour' && selectedHour !== undefined) {
        queryParams.append('hour', selectedHour);
      }

      const response = await fetch(`${BACKEND_URL}/api/analytics/sales-data?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setAnalyticsData(data);

      // Fetch category time series data for breakdown lines
      await fetchCategoryTimeSeries(startDate, endDate);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryTimeSeries = async (startDate, endDate) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        granularity: filters.granularity
      });

      if (filters.categoryId) queryParams.append('categoryId', filters.categoryId);
      if (filters.productId) queryParams.append('productId', filters.productId);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const response = await fetch(`${BACKEND_URL}/api/analytics/category-time-series?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setCategoryTimeSeriesData(data);
    } catch (err) {
      console.error('Error fetching category time series:', err);
      // Don't set error state, just log - this is optional data
    }
  };

  const fetchIndividualCategoryTimeSeries = async (startDate, endDate) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        granularity: filters.granularity
      });

      if (filters.categoryId) queryParams.append('categoryId', filters.categoryId);
      if (filters.productId) queryParams.append('productId', filters.productId);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const response = await fetch(`${BACKEND_URL}/api/analytics/individual-category-time-series?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setIndividualCategoryTimeSeriesData(data);
    } catch (err) {
      console.error('Error fetching individual category time series:', err);
      // Don't set error state, just log - this is optional data
    }
  };

  const fetchUserTimeSeries = async (startDate, endDate) => {
    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        granularity: filters.granularity,
        includeSpecials: 'true',
        includeDiscounts: 'true'
      });

      if (filters.categoryId) queryParams.append('categoryId', filters.categoryId);
      if (filters.productId) queryParams.append('productId', filters.productId);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const response = await fetch(`${BACKEND_URL}/api/analytics/user-time-series?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setUserTimeSeriesData(data);
    } catch (err) {
      console.error('Error fetching user time series:', err);
      // Don't set error state, just log - this is optional data
    }
  };

  const fetchTipsData = async (startDate, endDate) => {
    setTipsLoading(true);
    setTipsError(null);

    try {
      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        granularity: tipsGranularity
      });

      // Tips are attributed to the user who closed the order, so only the user
      // filter applies here. Category and product filters do not narrow a tip.
      if (filters.userId) queryParams.append('userId', filters.userId);

      const response = await fetch(`${BACKEND_URL}/api/analytics/tips?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setTipsData(data);
    } catch (err) {
      console.error('Error fetching tips data:', err);
      setTipsError(err.message);
    } finally {
      setTipsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      granularity: 'week',
      categoryId: '',
      productId: '',
      userId: ''
    });
    setTimePeriod('year');
    setSelectedCategoryGroup(null);
    setDrillDownData(null);
    setSelectedCategory(null);
    setProductDrillDownData(null);
  };

  // Legend click handler to toggle individual line visibility
  const handleLegendClick = (data) => {
    const { dataKey } = data;
    setHiddenLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataKey)) {
        newSet.delete(dataKey);
      } else {
        newSet.add(dataKey);
      }
      return newSet;
    });
  };

  // Zoom handlers for drag-to-zoom on time series chart
  const handleMouseDown = useCallback((e) => {
    if (e && e.activeLabel) {
      refAreaLeftRef.current = e.activeLabel;
      refAreaRightRef.current = e.activeLabel;
      setRefAreaTick(t => t + 1);
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (refAreaLeftRef.current && e && e.activeLabel) {
      refAreaRightRef.current = e.activeLabel;
      setRefAreaTick(t => t + 1);
    }
  }, []);

  const handleMouseUp = () => {
    const refAreaLeft = refAreaLeftRef.current;
    const refAreaRight = refAreaRightRef.current;

    if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight && analyticsData) {
      // Get the indices of the selected range
      const allPeriods = analyticsData.timeSeries.map(d => d.period);
      const leftIndex = allPeriods.indexOf(refAreaLeft);
      const rightIndex = allPeriods.indexOf(refAreaRight);

      // Ensure left is before right
      const startIndex = Math.min(leftIndex, rightIndex);
      const endIndex = Math.max(leftIndex, rightIndex);

      if (startIndex >= 0 && endIndex >= 0 && startIndex !== endIndex) {
        const startPeriod = allPeriods[startIndex];
        const endPeriod = allPeriods[endIndex];

        // Convert periods to dates based on granularity
        const convertPeriodToDate = (period) => {
          if (filters.granularity === 'hour') {
            // Format: '2024-01-15 14:00:00'
            return period.split(' ')[0];
          } else if (filters.granularity === 'week') {
            // Format: '2024-W03'
            const [year, week] = period.split('-W');
            const firstDayOfYear = new Date(parseInt(year), 0, 1);
            const daysOffset = (parseInt(week) - 1) * 7;
            const weekStart = new Date(firstDayOfYear);
            weekStart.setDate(firstDayOfYear.getDate() + daysOffset);
            return formatLocalDate(weekStart);
          } else if (filters.granularity === 'month') {
            // Format: '2024-03'
            return `${period}-01`;
          } else if (filters.granularity === 'dayOfWeek') {
            // Can't zoom on day of week
            return null;
          } else {
            // day or other
            return period;
          }
        };

        const startDate = convertPeriodToDate(startPeriod);
        const endDate = convertPeriodToDate(endPeriod);

        if (startDate && endDate) {
          // Calculate end date for the period
          let actualEndDate = endDate;
          if (filters.granularity === 'week') {
            const endDateObj = parseLocalDate(endDate);
            endDateObj.setDate(endDateObj.getDate() + 6);
            actualEndDate = formatLocalDate(endDateObj);
          } else if (filters.granularity === 'month') {
            const [year, month] = endPeriod.split('-');
            const endDateObj = new Date(parseInt(year), parseInt(month), 0);
            actualEndDate = formatLocalDate(endDateObj);
          }

          // Switch to 'day' granularity and custom time period
          setCustomDateRange({ start: startDate, end: actualEndDate });
          setTimePeriod('custom');
          setFilters(prev => ({ ...prev, granularity: 'day' }));
        }
      }
    }

    // Reset selection
    refAreaLeftRef.current = null;
    refAreaRightRef.current = null;
    setRefAreaTick(t => t + 1);
  };

  // (screenshot automation useEffect moved below handlePieClick)

  // Touch range selection handler (for DateRangeSlider)
  const handleTouchRangeSelect = (startPeriod, endPeriod) => {
    console.log('AdvancedAnalytics - handleTouchRangeSelect called:', {
      startPeriod,
      endPeriod,
      currentTimePeriod: timePeriod,
      granularity: filters.granularity,
      hasAnalyticsData: !!analyticsData
    });

    if (!analyticsData || startPeriod === endPeriod) {
      console.log('AdvancedAnalytics - Skipping (no data or same period)');
      return;
    }

    // Convert periods to dates based on granularity
    const convertPeriodToDate = (period, isEndDate = false) => {
      if (filters.granularity === 'hour') {
        // Format: '2024-01-15 14:00:00'
        return period.split(' ')[0];
      } else if (filters.granularity === 'week') {
        // Format: '2024-W03'
        const [year, week] = period.split('-W');
        const firstDayOfYear = new Date(parseInt(year), 0, 1);
        const daysOffset = (parseInt(week) - 1) * 7;
        const weekStart = new Date(firstDayOfYear);
        weekStart.setDate(firstDayOfYear.getDate() + daysOffset);

        // If this is the end date, add 6 days to get the last day of the week
        if (isEndDate) {
          weekStart.setDate(weekStart.getDate() + 6);
        }

        return formatLocalDate(weekStart);
      } else if (filters.granularity === 'month') {
        // Format: '2024-03'
        if (isEndDate) {
          // Get last day of the month
          const [year, month] = period.split('-');
          const lastDay = new Date(parseInt(year), parseInt(month), 0);
          return formatLocalDate(lastDay);
        }
        return `${period}-01`;
      } else if (filters.granularity === 'dayOfWeek') {
        // Can't zoom on day of week
        return null;
      } else {
        // day or other
        return period;
      }
    };

    const startDate = convertPeriodToDate(startPeriod, false);
    const endDate = convertPeriodToDate(endPeriod, true);

    console.log('AdvancedAnalytics - Converted dates:', { startDate, endDate });

    if (startDate && endDate) {
      // Use exact dates without modification
      // Keep the current granularity
      console.log('AdvancedAnalytics - Applying filter:', {
        start: startDate,
        end: endDate,
        granularity: filters.granularity
      });
      setCustomDateRange({ start: startDate, end: endDate });
      setTimePeriod('custom');
    } else {
      console.log('AdvancedAnalytics - Invalid dates, not applying filter');
    }
  };

  const fetchCategoryDetails = async (groupKey, groupName) => {
    try {
      // Calculate date range based on timePeriod (same as fetchAnalyticsData)
      let startDate, endDate;
      const now = new Date();

      if (timePeriod === 'custom') {
        startDate = customDateRange.start;
        endDate = customDateRange.end;
      } else if (timePeriod === 'all') {
        startDate = '2000-01-01';
        endDate = formatLocalDate(now);
      } else if (timePeriod === 'hour' || timePeriod === 'day') {
        startDate = selectedDate;
        endDate = selectedDate;
      } else if (timePeriod === 'week') {
        const selected = parseLocalDate(selectedDate);
        const dayOfWeek = selected.getDay();
        const startOfWeek = new Date(selected);
        startOfWeek.setDate(selected.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
      } else if (timePeriod === 'month') {
        const selected = parseLocalDate(selectedDate);
        const startOfMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
        const endOfMonth = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
        startDate = formatLocalDate(startOfMonth);
        endDate = formatLocalDate(endOfMonth);
      } else if (timePeriod === 'year') {
        const selected = parseLocalDate(selectedDate);
        startDate = `${selected.getFullYear()}-01-01`;
        endDate = `${selected.getFullYear()}-12-31`;
      } else {
        startDate = formatLocalDate(now);
        endDate = formatLocalDate(now);
      }

      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        categoryGroup: groupKey
      });

      if (filters.categoryId) queryParams.append('categoryId', filters.categoryId);
      if (filters.productId) queryParams.append('productId', filters.productId);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const response = await fetch(`${BACKEND_URL}/api/analytics/category-details?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // Add groupKey to the drilldown data for color lookup
      setDrillDownData({ ...data, groupKey });
      setSelectedCategoryGroup(groupName);

      // Automatically fetch products for the largest category
      if (data.categories && data.categories.length > 0) {
        // Sort by revenue to find the largest category
        const sortedCategories = [...data.categories].sort((a, b) => b.revenue - a.revenue);
        const largestCategory = sortedCategories[0];
        await fetchProductDetails(largestCategory.id, largestCategory.name);
      }
    } catch (err) {
      console.error('Error fetching category details:', err);
      setError(err.message);
    }
  };

  const fetchProductDetails = async (categoryId, categoryName) => {
    try {
      // Calculate date range based on timePeriod
      let startDate, endDate;
      const now = new Date();

      if (timePeriod === 'custom') {
        startDate = customDateRange.start;
        endDate = customDateRange.end;
      } else if (timePeriod === 'all') {
        startDate = '2000-01-01';
        endDate = formatLocalDate(now);
      } else if (timePeriod === 'hour' || timePeriod === 'day') {
        startDate = selectedDate;
        endDate = selectedDate;
      } else if (timePeriod === 'week') {
        const selected = parseLocalDate(selectedDate);
        const dayOfWeek = selected.getDay();
        const startOfWeek = new Date(selected);
        startOfWeek.setDate(selected.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = formatLocalDate(startOfWeek);
        endDate = formatLocalDate(endOfWeek);
      } else if (timePeriod === 'month') {
        const selected = parseLocalDate(selectedDate);
        const startOfMonth = new Date(selected.getFullYear(), selected.getMonth(), 1);
        const endOfMonth = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
        startDate = formatLocalDate(startOfMonth);
        endDate = formatLocalDate(endOfMonth);
      } else if (timePeriod === 'year') {
        const selected = parseLocalDate(selectedDate);
        startDate = `${selected.getFullYear()}-01-01`;
        endDate = `${selected.getFullYear()}-12-31`;
      } else {
        startDate = formatLocalDate(now);
        endDate = formatLocalDate(now);
      }

      const queryParams = new URLSearchParams({
        startDate,
        endDate,
        categoryId
      });

      if (filters.productId) queryParams.append('productId', filters.productId);
      if (filters.userId) queryParams.append('userId', filters.userId);

      const response = await fetch(`${BACKEND_URL}/api/analytics/product-details?${queryParams}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProductDrillDownData({ ...data, categoryName });
      setSelectedCategory(categoryId);
    } catch (err) {
      console.error('Error fetching product details:', err);
      setError(err.message);
    }
  };

  const handlePieClick = async (data) => {
    if (data && data.name) {
      // If clicking the already-selected group, deselect it
      if (selectedCategoryGroup === data.name) {
        setSelectedCategoryGroup(null);
        setDrillDownData(null);
        setSelectedCategory(null);
        setProductDrillDownData(null);
      } else {
        await fetchCategoryDetails(data.groupKey, data.name);
      }
    }
  };

  const handlePieHover = (data) => {
    if (data && data.name) {
      setHoveredGroup(data.name);
    }
  };

  // Re-fetch drill-down data when date range changes
  useEffect(() => {
    if (selectedCategoryGroup && drillDownData?.groupKey) {
      fetchCategoryDetails(drillDownData.groupKey, selectedCategoryGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod, customDateRange, selectedDate]);

  // Expose helpers for screenshot automation
  useEffect(() => {
    window.__analyticsZoom = {
      setRefAreaLeft: (v) => { refAreaLeftRef.current = v; setRefAreaTick(t => t + 1); },
      setRefAreaRight: (v) => { refAreaRightRef.current = v; setRefAreaTick(t => t + 1); },
      handleMouseUp
    };
    window.__analyticsPie = { handlePieClick, analyticsData };
    return () => { delete window.__analyticsZoom; delete window.__analyticsPie; };
  });

  // Get category names that belong to a group
  const getCategoriesForGroup = (groupName) => {
    if (!categories.length) return [];

    const groupKey = groupName.toLowerCase();
    const groupConfig = categoryGroups[groupKey];

    if (!groupConfig) return [];

    return categories.filter(cat => groupConfig.categoryIds.includes(cat.id));
  };

  // Drag and drop handlers
  const handleDragStart = (e, categoryId, sourceGroup) => {
    setDraggedCategory({ id: categoryId, sourceGroup });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetGroupKey) => {
    e.preventDefault();
    if (!draggedCategory) return;

    const workingGroups = tempGroups || categoryGroups;
    const newGroups = { ...workingGroups };

    // Remove from source group
    if (draggedCategory.sourceGroup) {
      newGroups[draggedCategory.sourceGroup].categoryIds =
        newGroups[draggedCategory.sourceGroup].categoryIds.filter(id => id !== draggedCategory.id);
    }

    // Add to target group if not null (null means unassigned)
    if (targetGroupKey && newGroups[targetGroupKey]) {
      if (!newGroups[targetGroupKey].categoryIds.includes(draggedCategory.id)) {
        newGroups[targetGroupKey].categoryIds.push(draggedCategory.id);
      }
    }

    setTempGroups(newGroups);
    setDraggedCategory(null);
  };

  const handleSaveGroupConfig = async () => {
    if (tempGroups) {
      try {
        setLoading(true);
        await saveCategoryGroupAssignments(tempGroups);
        setCategoryGroups(tempGroups);
        setTempGroups(null);
        setShowGroupConfig(false);
        // Refresh analytics data with new groupings
        await fetchAnalyticsData();
      } catch (err) {
        console.error('Error saving category groups:', err);
        setError('Failed to save category group assignments. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setShowGroupConfig(false);
    }
  };

  const handleCancelGroupConfig = () => {
    setTempGroups(null);
    setShowGroupConfig(false);
  };

  const handleOpenGroupConfig = () => {
    setTempGroups({ ...categoryGroups });
    setShowGroupConfig(true);
  };

  const handleNewGroup = () => {
    setEditingGroup(null);
    setGroupFormData({ group_key: '', name: '', color: '#1976d2', description: '' });
    setShowGroupForm(true);
  };

  const handleEditGroup = (groupKey) => {
    const group = tempGroups[groupKey];
    setEditingGroup(groupKey);
    setGroupFormData({
      group_key: groupKey,
      name: group.name,
      color: group.color,
      description: group.description || ''
    });
    setShowGroupForm(true);
  };

  const handleDeleteGroup = async (groupKey) => {
    if (!window.confirm(t('confirmDelete') + ` "${tempGroups[groupKey].name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteGroup(groupKey);

      // Reload groups from database
      await fetchCategoryGroupAssignments();
      setTempGroups(null);
      setShowGroupConfig(false);
      await fetchAnalyticsData();
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(`Failed to delete group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroupForm = async () => {
    try {
      setLoading(true);

      if (editingGroup) {
        // Update existing group
        await updateGroup(editingGroup, {
          name: groupFormData.name,
          color: groupFormData.color,
          description: groupFormData.description,
          display_order: tempGroups[editingGroup].displayOrder || 0
        });
      } else {
        // Create new group
        await createGroup({
          group_key: groupFormData.group_key,
          name: groupFormData.name,
          color: groupFormData.color,
          description: groupFormData.description,
          display_order: Object.keys(tempGroups || {}).length + 1
        });
      }

      // Reload groups from database
      await fetchCategoryGroupAssignments();

      // Update temp groups for immediate UI feedback
      const updatedGroups = { ...categoryGroups };
      if (!editingGroup) {
        updatedGroups[groupFormData.group_key] = {
          name: groupFormData.name,
          color: groupFormData.color,
          description: groupFormData.description,
          categoryIds: []
        };
      } else {
        updatedGroups[editingGroup] = {
          ...updatedGroups[editingGroup],
          name: groupFormData.name,
          color: groupFormData.color,
          description: groupFormData.description
        };
      }
      setTempGroups(updatedGroups);

      setShowGroupForm(false);
      setEditingGroup(null);
    } catch (err) {
      console.error('Error saving group:', err);
      setError(`Failed to save group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGroupForm = () => {
    setShowGroupForm(false);
    setEditingGroup(null);
    setGroupFormData({ group_key: '', name: '', color: '#1976d2', description: '' });
  };

  // Filter products by selected category
  const filteredProducts = filters.categoryId
    ? products.filter(p => p.category_id === parseInt(filters.categoryId))
    : products;

  // Helper function to group similar products (e.g., "Elephant Shoes 1.0" and "Elephant Shoes 2.0")
  const groupSimilarProducts = (products) => {
    if (!products || products.length === 0) return [];

    // Group products by base name (removing version numbers and common suffixes)
    const productGroups = {};

    products.forEach(product => {
      // Remove version numbers like "1.0", "2.0", "v1", "v2", etc. and trim
      // This handles: "Tarsius 1.0" -> "Tarsius", "Elephant Shoes 2.0" -> "Elephant Shoes"
      const baseName = product.name
        .replace(/\s*v?\d+(\.\d+)*$/i, '')  // Remove optional space + version at end (handles "Tarsius 1.0" and "Tarsius1.0")
        .trim();

      if (!productGroups[baseName]) {
        productGroups[baseName] = {
          name: baseName,
          revenue: 0,
          orderCount: 0,
          itemsSold: 0,
          products: []
        };
      }

      // Aggregate data
      productGroups[baseName].revenue += product.revenue;
      productGroups[baseName].orderCount += product.orderCount;
      productGroups[baseName].itemsSold += product.itemsSold;
      productGroups[baseName].products.push(product.name);
    });

    // Convert to array and calculate percentages
    const totalRevenue = Object.values(productGroups).reduce((sum, group) => sum + group.revenue, 0);
    const groupedProducts = Object.values(productGroups).map(group => ({
      ...group,
      percentage: totalRevenue > 0 ? ((group.revenue / totalRevenue) * 100).toFixed(1) : 0,
      avgPrice: group.orderCount > 0 ? group.revenue / group.itemsSold : 0
    }));

    return groupedProducts.sort((a, b) => b.revenue - a.revenue);
  };

  // Helper functions for date navigation (matching QuickViewStats style)
  const navigateDate = (direction) => {
    const current = parseLocalDate(selectedDate);

    if (timePeriod === 'day') {
      current.setDate(current.getDate() + direction);
    } else if (timePeriod === 'week') {
      current.setDate(current.getDate() + (direction * 7));
    } else if (timePeriod === 'month') {
      current.setMonth(current.getMonth() + direction);
    } else if (timePeriod === 'year') {
      current.setFullYear(current.getFullYear() + direction);
    }

    setSelectedDate(formatLocalDate(current));
  };

  // eslint-disable-next-line no-unused-vars
  const getDateRangeDisplay = () => {
    if (timePeriod === 'all') return t('allTime');
    if (!selectedDate) return '';

    const date = new Date(selectedDate);
    const formatDate = (d) => `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;

    if (timePeriod === 'hour') {
      const hour = selectedHour || 0;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${formatDate(date)} ${displayHour}:00 ${ampm}`;
    } else if (timePeriod === 'day') {
      return formatDate(date);
    } else if (timePeriod === 'week') {
      const dayOfWeek = date.getDay();
      const monday = new Date(date);
      monday.setDate(date.getDate() - dayOfWeek);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return `${formatDate(monday)} - ${formatDate(sunday)}`;
    } else if (timePeriod === 'month') {
      return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
    } else if (timePeriod === 'year') {
      return `${date.getFullYear()}`;
    }
    return '';
  };

  const navigateHour = (direction) => {
    const newHour = selectedHour + direction;

    if (newHour > 23) {
      setSelectedHour(0);
      navigateDate(1);
    } else if (newHour < 0) {
      setSelectedHour(23);
      navigateDate(-1);
    } else {
      setSelectedHour(newHour);
    }
  };

  const renderTimePeriodSelector = () => {
    const inputStyle = {
      padding: '0.5rem',
      borderRadius: '4px',
      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
      backgroundColor: theme?.colors?.surface || '#fff',
      color: theme?.colors?.text || '#000',
      fontSize: '0.9rem'
    };

    return (
      <div style={{
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Time Period Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {['hour', 'day', 'week', 'month', 'year', 'all'].map(period => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: timePeriod === period ? theme?.colors?.primary : theme?.colors?.surface,
                color: timePeriod === period ? theme?.colors?.surface : theme?.colors?.text,
                border: `1px solid ${theme?.colors?.border}`,
                borderRadius: theme?.borderRadius || 4,
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: timePeriod === period ? 'bold' : 'normal'
              }}
            >
              {period === 'hour' ? t('byHour') :
               period === 'day' ? t('byDay') :
               period === 'week' ? t('byWeek') :
               period === 'month' ? t('byMonth') :
               period === 'year' ? t('byYear') : t('allTime')}
            </button>
          ))}
        </div>

        {/* Date Navigation and Picker */}
        {timePeriod !== 'all' && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => timePeriod === 'hour' ? navigateHour(-1) : navigateDate(-1)}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: theme?.colors?.surface,
                color: theme?.colors?.text,
                border: `1px solid ${theme?.colors?.border}`,
                borderRadius: theme?.borderRadius || 4,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              ←
            </button>
            {/* Date/Month/Week picker based on time period */}
            {(timePeriod === 'hour' || timePeriod === 'day') && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  if (timePeriod === 'hour') {
                    setSelectedHour(0);
                  }
                }}
                style={inputStyle}
              />
            )}
            {timePeriod === 'week' && (
              <input
                type="week"
                value={`${selectedDate.substring(0, 4)}-W${getISOWeek(parseLocalDate(selectedDate)).toString().padStart(2, '0')}`}
                onChange={(e) => {
                  const [year, week] = e.target.value.split('-W');
                  const targetDate = getDateFromISOWeek(parseInt(year), parseInt(week));
                  setSelectedDate(formatLocalDate(targetDate));
                }}
                style={inputStyle}
              />
            )}
            {timePeriod === 'month' && (
              <input
                type="month"
                value={selectedDate.substring(0, 7)}
                onChange={(e) => {
                  setSelectedDate(e.target.value + '-01');
                }}
                style={inputStyle}
              />
            )}
            {timePeriod === 'year' && (
              <select
                value={parseLocalDate(selectedDate).getFullYear()}
                onChange={(e) => {
                  setSelectedDate(`${e.target.value}-01-01`);
                }}
                style={inputStyle}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            )}
            {timePeriod === 'hour' && (
              <select
                value={selectedHour}
                onChange={(e) => setSelectedHour(parseInt(e.target.value))}
                style={inputStyle}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => timePeriod === 'hour' ? navigateHour(1) : navigateDate(1)}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: theme?.colors?.surface,
                color: theme?.colors?.text,
                border: `1px solid ${theme?.colors?.border}`,
                borderRadius: theme?.borderRadius || 4,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              →
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderFilterPanel = () => {
    const inputStyle = {
      padding: '0.4rem 0.6rem',
      borderRadius: '4px',
      border: `1px solid ${theme?.colors?.border || '#ccc'}`,
      backgroundColor: theme?.colors?.surface || '#fff',
      color: theme?.colors?.text || '#000',
      fontSize: '0.85rem'
    };

    return (
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'center'
      }}>
        {/* Category Filter */}
        <select
          value={filters.categoryId}
          onChange={(e) => {
            handleFilterChange('categoryId', e.target.value);
            handleFilterChange('productId', '');
          }}
          style={inputStyle}
        >
          <option value="">{t('allCategories')}</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{catName(cat)}</option>
          ))}
        </select>

        {/* Product Filter */}
        <select
          value={filters.productId}
          onChange={(e) => handleFilterChange('productId', e.target.value)}
          style={inputStyle}
          disabled={!filteredProducts.length}
        >
          <option value="">{t('all')} {t('products')}</option>
          {filteredProducts.map(prod => (
            <option key={prod.id} value={prod.id}>{prod.name}</option>
          ))}
        </select>

        {/* User Filter */}
        <select
          value={filters.userId}
          onChange={(e) => handleFilterChange('userId', e.target.value)}
          style={inputStyle}
        >
          <option value="">{t('all')} {t('users')}</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>{user.user_name}</option>
          ))}
        </select>

        {/* Reset Button */}
        <button
          onClick={handleResetFilters}
          style={{
            padding: '0.4rem 0.75rem',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: theme?.colors?.secondary || '#666',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '0.8rem',
            marginLeft: 'auto'
          }}
        >
          {t('resetFilters')}
        </button>
      </div>
    );
  };

  const renderStatsSummary = () => {
    if (!analyticsData) return null;

    const statCardStyle = (isActive) => ({
      flex: '1',
      minWidth: '200px',
      backgroundColor: theme?.colors?.surface || '#fff',
      padding: '1.5rem',
      borderRadius: theme?.borderRadius || '8px',
      boxShadow: isActive ? `0 4px 8px ${theme?.colors?.primary || '#1976d2'}40` : theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
      textAlign: 'center',
      cursor: 'pointer',
      border: isActive ? `2px solid ${theme?.colors?.primary || '#1976d2'}` : '2px solid transparent',
      transition: 'all 0.2s'
    });

    return (
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setChartMetric('revenue')}
          style={statCardStyle(chartMetric === 'revenue')}
        >
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('totalRevenue')}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme?.colors?.primary || '#1976d2' }}>
            ${analyticsData.totalRevenue.toFixed(2)}
          </div>
        </button>

        <button
          onClick={() => setChartMetric('orders')}
          style={statCardStyle(chartMetric === 'orders')}
        >
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('totalOrders')}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme?.colors?.primary || '#1976d2' }}>
            {analyticsData.totalOrders}
          </div>
        </button>

        <button
          onClick={() => setChartMetric('items')}
          style={statCardStyle(chartMetric === 'items')}
        >
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('totalItems')}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme?.colors?.primary || '#1976d2' }}>
            {analyticsData.totalItems || 0}
          </div>
        </button>

        <button
          onClick={() => setChartMetric('avgOrderTotal')}
          style={statCardStyle(chartMetric === 'avgOrderTotal')}
        >
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('avgOrderTotal')}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: theme?.colors?.primary || '#1976d2' }}>
            ${analyticsData.avgOrderTotal.toFixed(2)}
          </div>
        </button>
      </div>
    );
  };

  const renderTimeSeriesChart = () => {
    if (!analyticsData || !analyticsData.timeSeries || analyticsData.timeSeries.length === 0) {
      return (
        <div style={{
          backgroundColor: theme?.colors?.surface || '#fff',
          padding: '2rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          textAlign: 'center',
          color: theme?.colors?.textSecondary || '#666'
        }}>
          {t('noData')}
        </div>
      );
    }

    const dataKey = chartMetric === 'revenue' ? 'revenue' :
                    chartMetric === 'items' ? 'itemsSold' :
                    chartMetric === 'avgOrderTotal' ? 'avgOrderTotal' : 'orderCount';
    const label = chartMetric === 'revenue' ? t('revenue') :
                  chartMetric === 'items' ? t('items') :
                  chartMetric === 'avgOrderTotal' ? t('avgOrderTotal') : t('orders');
    const metricSuffix = chartMetric === 'revenue' ? '_revenue' :
                         chartMetric === 'items' ? '_items' :
                         chartMetric === 'avgOrderTotal' ? '_avgOrderTotal' : '_orders';

    // Merge category time series data with main time series for chart rendering
    let chartData = analyticsData.timeSeries;

    // Merge by group data
    if (visibleLines.byGroup && categoryTimeSeriesData?.timeSeries) {
      const categoryDataMap = new Map();
      categoryTimeSeriesData.timeSeries.forEach(item => {
        categoryDataMap.set(item.period, item);
      });
      chartData = chartData.map(dataPoint => {
        const categoryData = categoryDataMap.get(dataPoint.period) || {};
        return { ...dataPoint, ...categoryData };
      });
    }

    // Merge individual category data
    if (visibleLines.byCategory && individualCategoryTimeSeriesData?.timeSeries) {
      const categoryDataMap = new Map();
      individualCategoryTimeSeriesData.timeSeries.forEach(item => {
        categoryDataMap.set(item.period, item);
      });
      chartData = chartData.map(dataPoint => {
        const categoryData = categoryDataMap.get(dataPoint.period) || {};
        return { ...dataPoint, ...categoryData };
      });
    }

    // Merge user data
    if (visibleLines.byUser && userTimeSeriesData?.timeSeries) {
      const userDataMap = new Map();
      userTimeSeriesData.timeSeries.forEach(item => {
        userDataMap.set(item.period, item);
      });
      chartData = chartData.map(dataPoint => {
        const userData = userDataMap.get(dataPoint.period) || {};
        return { ...dataPoint, ...userData };
      });
    }

    return (
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text || '#000' }}>
              {chartMetric === 'revenue' ? t('revenueOverTime') :
               chartMetric === 'items' ? t('itemsOverTime') :
               chartMetric === 'avgOrderTotal' ? t('avgOrderTotalOverTime') :
               t('ordersOverTime')}
            </h3>
            {/* Granularity Buttons */}
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {['hour', 'day', 'dayOfWeek', 'week', 'month'].map(granularity => (
                <button
                  key={granularity}
                  onClick={() => handleFilterChange('granularity', granularity)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    backgroundColor: filters.granularity === granularity ? theme?.colors?.primary : theme?.colors?.surface,
                    color: filters.granularity === granularity ? '#fff' : theme?.colors?.text,
                    border: `1px solid ${theme?.colors?.border}`,
                    borderRadius: theme?.borderRadius || 4,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: filters.granularity === granularity ? 'bold' : 'normal'
                  }}
                >
                  {granularity === 'hour' ? t('hour') :
                   granularity === 'day' ? t('day') :
                   granularity === 'dayOfWeek' ? t('dayOfWeek') :
                   granularity === 'week' ? t('week') : t('month')}
                </button>
              ))}
            </div>
          </div>

          {/* Legend Checkboxes */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            padding: '0.5rem',
            backgroundColor: theme?.colors?.background || '#f5f5f5',
            borderRadius: '4px',
            fontSize: '0.85rem'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={visibleLines.total}
                onChange={(e) => setVisibleLines(prev => ({ ...prev, total: e.target.checked }))}
                style={{ cursor: 'pointer' }}
              />
              {t('total')} {label}
            </label>
            {categoryTimeSeriesData?.categoryGroups && categoryTimeSeriesData.categoryGroups.length > 0 && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                color: theme?.colors?.text || '#000'
              }}>
                <input
                  type="checkbox"
                  checked={visibleLines.byGroup}
                  onChange={(e) => setVisibleLines(prev => ({ ...prev, byGroup: e.target.checked }))}
                  style={{ cursor: 'pointer' }}
                />
                {t('byGroup')}
              </label>
            )}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={visibleLines.byCategory}
                onChange={(e) => setVisibleLines(prev => ({ ...prev, byCategory: e.target.checked }))}
                style={{ cursor: 'pointer' }}
              />
              {t('byCategory')}
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={visibleLines.byUser}
                onChange={(e) => setVisibleLines(prev => ({ ...prev, byUser: e.target.checked }))}
                style={{ cursor: 'pointer' }}
              />
              {t('byUser')}
            </label>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            onMouseDown={!isTouchDevice ? handleMouseDown : undefined}
            onMouseMove={!isTouchDevice ? handleMouseMove : undefined}
            onMouseUp={!isTouchDevice ? handleMouseUp : undefined}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_COLORS.grid}
            />
            <XAxis
              dataKey="period"
              stroke={CHART_COLORS.text}
              style={{ fontSize: '0.85rem' }}
            />
            <YAxis
              stroke={CHART_COLORS.text}
              style={{ fontSize: '0.85rem' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: CHART_COLORS.surface,
                border: `1px solid ${CHART_COLORS.grid}`,
                borderRadius: '4px',
                color: CHART_COLORS.text
              }}
              formatter={(value, name) => {
                if (chartMetric === 'revenue') return [`$${value.toFixed(2)}`, name];
                return [value, name];
              }}
            />
            <Legend
              wrapperStyle={{ color: CHART_COLORS.text, cursor: 'pointer' }}
              onClick={handleLegendClick}
            />

            {/* Total line */}
            {visibleLines.total && (
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={CHART_COLORS.line}
                strokeWidth={2}
                dot={{ fill: CHART_COLORS.line }}
                name={t('total') + ' ' + label}
                hide={hiddenLines.has(dataKey)}
              />
            )}

            {/* Group breakdown lines */}
            {visibleLines.byGroup && categoryTimeSeriesData?.categoryGroups &&
              categoryTimeSeriesData.categoryGroups.map(group => {
                const lineDataKey = `${group.key}${metricSuffix}`;
                return (
                  <Line
                    key={group.key}
                    type="monotone"
                    dataKey={lineDataKey}
                    stroke={group.color}
                    strokeWidth={1.5}
                    dot={{ fill: group.color, r: 3 }}
                    name={group.name}
                    hide={hiddenLines.has(lineDataKey)}
                  />
                );
              })
            }

            {/* Individual category breakdown lines */}
            {visibleLines.byCategory && individualCategoryTimeSeriesData?.categories &&
              individualCategoryTimeSeriesData.categories.map(category => {
                const lineDataKey = `${category.key}${metricSuffix}`;
                return (
                  <Line
                    key={category.key}
                    type="monotone"
                    dataKey={lineDataKey}
                    stroke={category.color}
                    strokeWidth={1.5}
                    dot={{ fill: category.color, r: 3 }}
                    name={category.name}
                    hide={hiddenLines.has(lineDataKey)}
                  />
                );
              })
            }

            {/* User breakdown lines */}
            {visibleLines.byUser && userTimeSeriesData?.users &&
              userTimeSeriesData.users.map(user => {
                const lineDataKey = `${user.key}${metricSuffix}`;
                return (
                  <Line
                    key={user.key}
                    type="monotone"
                    dataKey={lineDataKey}
                    stroke={user.color}
                    strokeWidth={1.5}
                    dot={{ fill: user.color, r: 3 }}
                    name={user.name}
                    hide={hiddenLines.has(lineDataKey)}
                  />
                );
              })
            }

            {/* Zoom selection area */}
            {refAreaLeftRef.current && refAreaRightRef.current && (
              <ReferenceArea
                x1={refAreaLeftRef.current}
                x2={refAreaRightRef.current}
                strokeOpacity={0.3}
                fill={theme?.colors?.primary || '#1976d2'}
                fillOpacity={0.3}
              />
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* Touch-friendly date range slider (only on touch devices) */}
        {isTouchDevice && analyticsData && chartData && chartData.length > 1 && (
          <div>
            {timePeriod === 'custom' && (
              <button
                onClick={() => {
                  setCustomDateRange(null);
                  setTimePeriod('allTime');
                }}
                style={{
                  padding: '8px 16px',
                  marginBottom: '10px',
                  backgroundColor: theme?.colors?.error || '#f44336',
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('clear')} {t('filter')}
              </button>
            )}
            <DateRangeSlider
              data={chartData}
              onRangeSelect={handleTouchRangeSelect}
              theme={theme}
              orientation="horizontal"
            />
          </div>
        )}
      </div>
    );
  };

  // Custom label renderer for pie charts with smaller text
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, percentage }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={CHART_COLORS.text}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="11px"
      >
        {`${name}: ${percentage}%`}
      </text>
    );
  };

  const renderCategoryPieChart = () => {
    if (!analyticsData || !analyticsData.categoryBreakdown || analyticsData.categoryBreakdown.length === 0) {
      return null;
    }

    // Custom tooltip for main pie chart - shows categories in the group
    const GroupTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        const groupCategories = getCategoriesForGroup(data.name);

        return (
          <div style={{
            backgroundColor: CHART_COLORS.surface,
            border: `2px solid ${CHART_COLORS[data.groupKey] || CHART_COLORS.other}`,
            borderRadius: '8px',
            padding: '1rem',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            maxWidth: '320px'
          }}>
            <div style={{
              fontWeight: 'bold',
              fontSize: '1.1rem',
              color: CHART_COLORS[data.groupKey] || CHART_COLORS.other,
              marginBottom: '0.5rem'
            }}>
              {data.name}
            </div>
            <div style={{ fontSize: '0.9rem', color: CHART_COLORS.text, marginBottom: '0.5rem' }}>
              <div><strong>{t('revenue')}:</strong> ${data.revenue.toFixed(2)}</div>
              <div><strong>{t('percentage')}:</strong> {data.percentage}%</div>
              <div><strong>{t('orders')}:</strong> {data.orderCount}</div>
            </div>
            {groupCategories.length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: `1px solid ${CHART_COLORS.grid}`
              }}>
                <div style={{
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  color: CHART_COLORS.text,
                  marginBottom: '0.25rem'
                }}>
                  {t('categories')}:
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: CHART_COLORS.textSecondary || '#666',
                  maxHeight: '120px',
                  overflowY: 'auto'
                }}>
                  {groupCategories.map((cat, idx) => (
                    <div key={cat.id} style={{ marginBottom: '2px' }}>
                      • {catName(cat)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{
              marginTop: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: `1px solid ${CHART_COLORS.grid}`,
              fontSize: '0.75rem',
              color: CHART_COLORS.textSecondary || '#666',
              fontStyle: 'italic'
            }}>
              {t('hoverToSeeBreakdown')}
            </div>
          </div>
        );
      }
      return null;
    };

    // Custom tooltip for detail pie chart (categories)
    const DetailTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;

        return (
          <div style={{
            backgroundColor: CHART_COLORS.surface,
            border: `2px solid ${CHART_COLORS.grid}`,
            borderRadius: '8px',
            padding: '0.75rem',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            maxWidth: '250px'
          }}>
            <div style={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: CHART_COLORS.text,
              marginBottom: '0.25rem'
            }}>
              {data.name}
            </div>
            <div style={{ fontSize: '0.85rem', color: CHART_COLORS.text }}>
              <div>${data.revenue.toFixed(2)} ({data.percentage}%)</div>
              <div>{data.orderCount} {t('orders')} • {data.itemsSold} {t('items')}</div>
            </div>
            <div style={{
              marginTop: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: `1px solid ${CHART_COLORS.grid}`,
              fontSize: '0.75rem',
              color: CHART_COLORS.textSecondary || '#666',
              fontStyle: 'italic'
            }}>
              {t('clickToViewDetails')}
            </div>
          </div>
        );
      }
      return null;
    };

    // Custom tooltip for product pie chart (third level)
    const ProductTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;

        return (
          <div style={{
            backgroundColor: CHART_COLORS.surface,
            border: `2px solid ${CHART_COLORS.grid}`,
            borderRadius: '8px',
            padding: '0.75rem',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            maxWidth: '280px'
          }}>
            <div style={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: CHART_COLORS.text,
              marginBottom: '0.25rem'
            }}>
              {data.name}
            </div>
            <div style={{ fontSize: '0.85rem', color: CHART_COLORS.text }}>
              <div><strong>{t('revenue')}:</strong> ${data.revenue.toFixed(2)} ({data.percentage}%)</div>
              <div><strong>{t('orders')}:</strong> {data.orderCount} • <strong>{t('items')}:</strong> {data.itemsSold}</div>
              <div><strong>{t('avgOrderTotal')}:</strong> ${data.avgPrice?.toFixed(2) || '0.00'}</div>
            </div>
            {data.products && data.products.length > 1 && (
              <div style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: `1px solid ${CHART_COLORS.grid}`,
                fontSize: '0.75rem',
                color: CHART_COLORS.textSecondary || '#666'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {t('includes')}:
                </div>
                <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                  {data.products.map((productName, idx) => (
                    <div key={idx} style={{ marginBottom: '2px' }}>
                      • {productName}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.isOther && data.otherProducts && data.otherProducts.length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: `1px solid ${CHART_COLORS.grid}`,
                fontSize: '0.75rem',
                color: CHART_COLORS.textSecondary || '#666'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {t('includes')} ({data.otherProducts.length} {t('products')}):
                </div>
                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                  {data.otherProducts.map((product, idx) => (
                    <div key={idx} style={{ marginBottom: '3px', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span>• {product.name}</span>
                      <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>${product.revenue.toFixed(2)} ({product.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }
      return null;
    };

    return (
      <div data-testid="pie-chart-section" style={{ display: 'flex', gap: '1rem', flexWrap: 'nowrap', overflowX: 'auto' }}>
        {/* Main Pie Chart */}
        <div style={{
          flex: '1 1 0',
          minWidth: '280px',
          maxWidth: '33.333%',
          backgroundColor: theme?.colors?.surface || '#fff',
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 style={{ margin: 0, color: theme?.colors?.text || '#000' }}>
              {t('salesByGroup')}
            </h3>
            <button
              onClick={handleOpenGroupConfig}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                backgroundColor: theme?.colors?.surface || '#fff',
                color: theme?.colors?.text || '#000',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
              title={t('configure')}
            >
              ⚙️
            </button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analyticsData.categoryBreakdown}
                dataKey="revenue"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                label={renderPieLabel}
                labelLine={{ stroke: CHART_COLORS.text }}
                onClick={handlePieClick}
                onMouseEnter={handlePieHover}
                onMouseLeave={() => setHoveredGroup(null)}
                style={{ cursor: 'pointer' }}
              >
                {analyticsData.categoryBreakdown.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS[entry.groupKey] || CHART_COLORS.other}
                    opacity={hoveredGroup && hoveredGroup !== entry.name ? 0.5 : 1}
                  />
                ))}
              </Pie>
              <Tooltip content={<GroupTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Custom clickable legend */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            justifyContent: 'center',
            marginTop: '1rem',
            padding: '0.5rem'
          }}>
            {analyticsData.categoryBreakdown.map((entry, index) => (
              <div
                key={`legend-${index}`}
                onClick={() => handlePieClick(entry)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  backgroundColor: selectedCategoryGroup === entry.name
                    ? `${CHART_COLORS[entry.groupKey] || CHART_COLORS.other}33`
                    : 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${CHART_COLORS[entry.groupKey] || CHART_COLORS.other}22`}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedCategoryGroup === entry.name
                  ? `${CHART_COLORS[entry.groupKey] || CHART_COLORS.other}33`
                  : 'transparent'}
              >
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: CHART_COLORS[entry.groupKey] || CHART_COLORS.other,
                  borderRadius: '2px'
                }} />
                <span style={{
                  fontSize: '0.85rem',
                  color: CHART_COLORS.text,
                  fontWeight: selectedCategoryGroup === entry.name ? 'bold' : 'normal'
                }}>
                  {entry.name}
                </span>
              </div>
            ))}
          </div>

          {/* Summary totals for first pie chart */}
          <div style={{
            marginTop: 'auto',
            paddingTop: '1rem',
            borderTop: `1px solid ${CHART_COLORS.grid}`,
            fontSize: '0.9rem',
            color: CHART_COLORS.text,
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            {t('total')}: ${analyticsData.totalRevenue.toFixed(2)} • {analyticsData.totalOrders} {t('orders')} • {analyticsData.totalItems || 0} {t('items')}
          </div>
        </div>

        {/* Detail Pie Chart - Shows on hover or click */}
        {drillDownData && drillDownData.categories.length > 0 && (
          <div style={{
            flex: '1 1 0',
            minWidth: '280px',
            maxWidth: '33.333%',
            backgroundColor: theme?.colors?.surface || '#fff',
            padding: '1rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: theme?.colors?.text || '#000' }}>
              {drillDownData.categoryGroup} {t('group')} {t('breakdown')}
            </h3>

            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={drillDownData.categories}
                  dataKey="revenue"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={renderPieLabel}
                  labelLine={{ stroke: CHART_COLORS.text }}
                  onClick={(data) => {
                    if (data && data.id) {
                      fetchProductDetails(data.id, data.name);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {drillDownData.categories.map((entry, index) => {
                    // Generate color shades based on parent group color
                    const baseColor = CHART_COLORS[drillDownData.groupKey] || CHART_COLORS.primary;
                    // Create variations by adjusting opacity/lightness
                    const colors = [
                      baseColor,
                      `${baseColor}dd`, // Slightly transparent
                      `${baseColor}bb`,
                      `${baseColor}99`,
                      `${baseColor}77`
                    ];
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={colors[index % colors.length]}
                      />
                    );
                  })}
                </Pie>
                <Tooltip content={<DetailTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            {/* Custom clickable legend */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              justifyContent: 'center',
              marginTop: '1rem',
              padding: '0.5rem'
            }}>
              {drillDownData.categories.map((entry, index) => {
                const baseColor = CHART_COLORS[drillDownData.groupKey] || CHART_COLORS.primary;
                const colors = [
                  baseColor,
                  `${baseColor}dd`,
                  `${baseColor}bb`,
                  `${baseColor}99`,
                  `${baseColor}77`
                ];
                return (
                  <div
                    key={`legend-${index}`}
                    onClick={() => {
                      if (entry.id) {
                        fetchProductDetails(entry.id, entry.name);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      backgroundColor: selectedCategory === entry.id
                        ? `${colors[index % colors.length]}33`
                        : 'transparent'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${colors[index % colors.length]}22`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedCategory === entry.id
                      ? `${colors[index % colors.length]}33`
                      : 'transparent'}
                  >
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: colors[index % colors.length],
                      borderRadius: '2px'
                    }} />
                    <span style={{
                      fontSize: '0.85rem',
                      color: CHART_COLORS.text,
                      fontWeight: selectedCategory === entry.id ? 'bold' : 'normal'
                    }}>
                      {entry.name}
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{
              marginTop: 'auto',
              paddingTop: '1rem',
              borderTop: `1px solid ${CHART_COLORS.grid}`,
              fontSize: '0.9rem',
              color: CHART_COLORS.text,
              fontWeight: 'bold'
            }}>
              {t('total')}: ${drillDownData.totalRevenue.toFixed(2)} • {drillDownData.totalOrders} {t('orders')} • {drillDownData.totalItems || 0} {t('items')}
            </div>
          </div>
        )}

        {/* Product Pie Chart - Third level drill-down */}
        {productDrillDownData && productDrillDownData.products.length > 0 && (() => {
          // Group similar products
          const groupedProducts = groupSimilarProducts(productDrillDownData.products);

          // Calculate total revenue for percentage calculation
          const totalRevenue = groupedProducts.reduce((sum, p) => sum + p.revenue, 0);

          // Separate products into main (≥1%) and other (<1%)
          const mainProducts = [];
          const otherProducts = [];

          groupedProducts.forEach(product => {
            const percentage = totalRevenue > 0 ? (product.revenue / totalRevenue) * 100 : 0;
            if (percentage >= 1) {
              mainProducts.push({ ...product, percentage: percentage.toFixed(1) });
            } else {
              otherProducts.push({ ...product, percentage: percentage.toFixed(1) });
            }
          });

          // Create "Other" entry if there are small products
          let displayProducts = mainProducts;
          if (otherProducts.length > 0) {
            const otherEntry = {
              name: t('other'),
              revenue: otherProducts.reduce((sum, p) => sum + p.revenue, 0),
              orderCount: otherProducts.reduce((sum, p) => sum + p.orderCount, 0),
              itemsSold: otherProducts.reduce((sum, p) => sum + p.itemsSold, 0),
              percentage: otherProducts.reduce((sum, p) => sum + parseFloat(p.percentage), 0).toFixed(1),
              isOther: true,
              otherProducts: otherProducts // Store breakdown for tooltip
            };
            displayProducts = [...mainProducts, otherEntry];
          }

          const totalOrders = displayProducts.reduce((sum, p) => sum + p.orderCount, 0);
          const totalItems = displayProducts.reduce((sum, p) => sum + p.itemsSold, 0);

          return (
            <div style={{
              flex: '1 1 0',
              minWidth: '280px',
              maxWidth: '33.333%',
              backgroundColor: theme?.colors?.surface || '#fff',
              padding: '1rem',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: theme?.colors?.text || '#000' }}>
                {productDrillDownData.categoryName} {t('products')}
              </h3>

              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={displayProducts}
                    dataKey="revenue"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={renderPieLabel}
                    labelLine={{ stroke: CHART_COLORS.text }}
                  >
                    {displayProducts.map((entry, index) => {
                      // Generate different color shades for products
                      const baseColor = drillDownData
                        ? (CHART_COLORS[drillDownData.groupKey] || CHART_COLORS.primary)
                        : CHART_COLORS.primary;
                      // Create more variations for products
                      const colors = [
                        baseColor,
                        `${baseColor}ee`,
                        `${baseColor}cc`,
                        `${baseColor}aa`,
                        `${baseColor}88`,
                        `${baseColor}66`
                      ];
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={colors[index % colors.length]}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip content={<ProductTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              {/* Custom legend (non-clickable for products since they're the final level) */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem',
                justifyContent: 'center',
                marginTop: '1rem',
                padding: '0.5rem'
              }}>
                {displayProducts.map((entry, index) => {
                  const baseColor = drillDownData
                    ? (CHART_COLORS[drillDownData.groupKey] || CHART_COLORS.primary)
                    : CHART_COLORS.primary;
                  const colors = [
                    baseColor,
                    `${baseColor}ee`,
                    `${baseColor}cc`,
                    `${baseColor}aa`,
                    `${baseColor}88`,
                    `${baseColor}66`
                  ];
                  return (
                    <div
                      key={`legend-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px'
                      }}
                    >
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: colors[index % colors.length],
                        borderRadius: '2px'
                      }} />
                      <span style={{
                        fontSize: '0.85rem',
                        color: CHART_COLORS.text
                      }}>
                        {entry.name}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div style={{
                marginTop: 'auto',
                paddingTop: '1rem',
                borderTop: `1px solid ${CHART_COLORS.grid}`,
                fontSize: '0.9rem',
                color: CHART_COLORS.text,
                fontWeight: 'bold'
              }}>
                {t('total')}: ${totalRevenue.toFixed(2)} • {totalOrders} {t('orders')} • {totalItems} {t('items')}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const renderTipsReport = () => {
    const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;

    const granularityButtonStyle = (isActive) => ({
      padding: '0.3rem 0.6rem',
      backgroundColor: isActive ? theme?.colors?.primary : theme?.colors?.surface,
      color: isActive ? '#fff' : theme?.colors?.text,
      border: `1px solid ${theme?.colors?.border}`,
      borderRadius: theme?.borderRadius || 4,
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontWeight: isActive ? 'bold' : 'normal'
    });

    const tileStyle = {
      flex: '1',
      minWidth: '150px',
      backgroundColor: theme?.colors?.background || '#f5f5f5',
      padding: '1rem',
      borderRadius: theme?.borderRadius || '8px',
      textAlign: 'center'
    };

    const tileLabelStyle = {
      fontSize: '0.85rem',
      color: theme?.colors?.textSecondary || '#666',
      marginBottom: '0.35rem'
    };

    const tileValueStyle = {
      fontSize: '1.6rem',
      fontWeight: 'bold',
      color: theme?.colors?.primary || '#1976d2'
    };

    const headerCellStyle = {
      textAlign: 'left',
      padding: '0.5rem',
      borderBottom: `1px solid ${theme?.colors?.border || '#e0e0e0'}`,
      color: theme?.colors?.textSecondary || '#666',
      fontSize: '0.8rem',
      fontWeight: 'normal'
    };

    const cellStyle = {
      padding: '0.5rem',
      borderBottom: `1px solid ${theme?.colors?.border || '#e0e0e0'}`,
      color: theme?.colors?.text || '#000',
      fontSize: '0.9rem'
    };

    // Both breakdowns carry the same columns, so they share one renderer
    const renderBreakdown = (title, columnLabel, rows, labelKey, rowKey) => (
      <div style={{ flex: '1', minWidth: '280px' }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text || '#000', fontSize: '0.95rem' }}>
          {title}
        </h4>
        {rows.length === 0 ? (
          <div style={{ color: theme?.colors?.textSecondary || '#666', fontSize: '0.9rem' }}>
            {t('noData')}
          </div>
        ) : (
          // Four columns do not fit a phone once a server name runs long, so the
          // table scrolls inside its card rather than widening the page
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>{columnLabel}</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>{t('tips')}</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>{t('orders')}</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>{t('avgTipPerOrder')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row[rowKey] === null ? 'unknown' : row[rowKey]}>
                    <td style={cellStyle}>{row[labelKey] || t('unknown')}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{formatCurrency(row.tipTotal)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{row.orderCount}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{formatCurrency(row.avgTip)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );

    const timeSeries = tipsData?.timeSeries || [];

    return (
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <h3 style={{ margin: 0, color: theme?.colors?.text || '#000' }}>
            {t('tipsReport')}
          </h3>
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            {['day', 'week', 'month'].map(granularity => (
              <button
                key={granularity}
                onClick={() => setTipsGranularity(granularity)}
                style={granularityButtonStyle(tipsGranularity === granularity)}
              >
                {granularity === 'day' ? t('byDay') :
                 granularity === 'week' ? t('byWeek') : t('byMonth')}
              </button>
            ))}
          </div>
        </div>

        {tipsError && (
          <div style={{
            backgroundColor: theme?.colors?.error || '#f44336',
            color: '#fff',
            padding: '0.75rem',
            borderRadius: '4px'
          }}>
            {t('error')}: {tipsError}
          </div>
        )}

        {!tipsError && tipsLoading && (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: theme?.colors?.textSecondary || '#666'
          }}>
            {t('loading')}...
          </div>
        )}

        {!tipsError && !tipsLoading && timeSeries.length === 0 && (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: theme?.colors?.textSecondary || '#666'
          }}>
            {t('noData')}
          </div>
        )}

        {!tipsError && !tipsLoading && timeSeries.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              <div style={tileStyle}>
                <div style={tileLabelStyle}>{t('totalTips')}</div>
                <div style={tileValueStyle}>{formatCurrency(tipsData.totalTips)}</div>
              </div>
              <div style={tileStyle}>
                <div style={tileLabelStyle}>{t('avgTipPerOrder')}</div>
                <div style={tileValueStyle}>{formatCurrency(tipsData.avgTipPerOrder)}</div>
              </div>
              <div style={tileStyle}>
                <div style={tileLabelStyle}>{t('tippedOrders')}</div>
                <div style={tileValueStyle}>{tipsData.tippedOrders} / {tipsData.totalOrders}</div>
              </div>
            </div>

            <h4 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text || '#000', fontSize: '0.95rem' }}>
              {t('tipsOverTime')}
            </h4>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={timeSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                <XAxis
                  dataKey="period"
                  stroke={CHART_COLORS.text}
                  style={{ fontSize: '0.85rem' }}
                />
                <YAxis
                  stroke={CHART_COLORS.text}
                  style={{ fontSize: '0.85rem' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: CHART_COLORS.surface,
                    border: `1px solid ${CHART_COLORS.grid}`,
                    borderRadius: '4px',
                    color: CHART_COLORS.text
                  }}
                  formatter={(value, name) => [formatCurrency(value), name]}
                />
                <Legend wrapperStyle={{ color: CHART_COLORS.text }} />
                <Line
                  type="monotone"
                  dataKey="tipTotal"
                  stroke={CHART_COLORS.line}
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS.line }}
                  name={t('tips')}
                />
              </LineChart>
            </ResponsiveContainer>

            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
              {renderBreakdown(t('tipsByServer'), t('server'), tipsData.byUser || [], 'userName', 'userId')}
              {renderBreakdown(t('tipsByPaymentMethod'), t('paymentMethod'), tipsData.byPaymentType || [], 'paymentTypeName', 'paymentTypeId')}
            </div>
          </>
        )}
      </div>
    );
  };

  // Memoize expensive render sections to avoid recomputing during drag-to-zoom
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedStatsSummary = useMemo(() => renderStatsSummary(), [analyticsData, theme, chartMetric]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFilterPanel = useMemo(() => renderFilterPanel(), [filters, categories, products, users, theme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCategoryPieChart = useMemo(() => renderCategoryPieChart(), [analyticsData, CHART_COLORS, selectedCategoryGroup, drillDownData, productDrillDownData, hoveredGroup, selectedCategory, theme, categoryGroups, categories]);

  return (
    <div style={{
      backgroundColor: theme?.colors?.background || '#f5f5f5',
      padding: '1rem'
    }}>
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: theme?.colors?.text || '#000'
        }}>
          {t('loading')}...
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: theme?.colors?.error || '#f44336',
          color: '#fff',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {t('error')}: {error}
        </div>
      )}

      {!loading && !error && analyticsData && (
        <>
          {renderTimePeriodSelector()}
          {memoizedStatsSummary}
          {renderTimeSeriesChart()}
          {renderTipsReport()}
          {memoizedFilterPanel}
          {memoizedCategoryPieChart}
        </>
      )}

      {/* Category Group Configuration Modal */}
      {showGroupConfig && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: theme?.colors?.text || '#000' }}>
                {t('configure')} {t('salesByGroup')}
              </h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleNewGroup}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    border: `1px solid ${theme?.colors?.primary || '#1976d2'}`,
                    backgroundColor: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.primary || '#1976d2',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ➕ {t('newGroup')}
                </button>
                <button
                  onClick={handleSaveGroupConfig}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: theme?.colors?.primary || '#1976d2',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {t('save')}
                </button>
                <button
                  onClick={handleCancelGroupConfig}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: theme?.colors?.secondary || '#666',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel')}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem', color: theme?.colors?.textSecondary || '#666', fontSize: '0.9rem' }}>
              {t('dragCategoriesToAssignGroups')}
            </div>

            {/* Group sections */}
            {Object.entries(tempGroups || categoryGroups).map(([groupKey, group]) => {
              const displayGroups = tempGroups || categoryGroups;
              const groupCategories = categories.filter(cat => displayGroups[groupKey].categoryIds.includes(cat.id));

              return (
              <div
                key={groupKey}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, groupKey)}
                style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: theme?.colors?.background || '#f5f5f5',
                  borderRadius: '8px',
                  border: `2px solid ${group.color}`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: group.color,
                      borderRadius: '4px',
                      marginRight: '0.5rem'
                    }} />
                    <h3 style={{ margin: 0, color: theme?.colors?.text || '#000' }}>
                      {group.name}
                    </h3>
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
                      ({group.description})
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEditGroup(groupKey)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.text || '#000',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title={t('edit')}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(groupKey)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: `1px solid ${theme?.colors?.error || '#f44336'}`,
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.error || '#f44336',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title={t('delete')}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, groupKey)}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    marginTop: '0.75rem',
                    minHeight: '60px',
                    padding: '0.5rem',
                    backgroundColor: theme?.colors?.surface || '#fff',
                    borderRadius: '4px',
                    border: draggedCategory ? `2px dashed ${theme?.colors?.primary || '#1976d2'}` : 'none'
                  }}
                >
                  {groupCategories.map(cat => (
                    <div
                      key={cat.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, cat.id, groupKey)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: group.color,
                        color: '#fff',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        cursor: 'grab',
                        opacity: draggedCategory?.id === cat.id ? 0.5 : 1
                      }}
                    >
                      {catName(cat)}
                    </div>
                  ))}
                  {groupCategories.length === 0 && !draggedCategory && (
                    <div style={{ color: theme?.colors?.textSecondary || '#666', fontStyle: 'italic', fontSize: '0.85rem' }}>
                      {t('dropCategoriesHere')}
                    </div>
                  )}
                </div>
              </div>
            );
            })}

            {/* Unassigned categories */}
            <div
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, null)}
              style={{
                marginTop: '2rem',
                padding: '1rem',
                backgroundColor: theme?.colors?.background || '#f5f5f5',
                borderRadius: '8px',
                border: `2px solid ${theme?.colors?.border || '#ccc'}`
              }}
            >
              <h3 style={{ margin: '0 0 0.75rem 0', color: theme?.colors?.text || '#000' }}>
                {t('unassignedCategories')}
              </h3>
              <div
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, null)}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  borderRadius: '4px',
                  minHeight: '60px',
                  border: draggedCategory ? `2px dashed ${theme?.colors?.border || '#ccc'}` : 'none'
                }}
              >
                {categories.filter(cat => {
                  // Check if category doesn't match any group
                  const displayGroups = tempGroups || categoryGroups;
                  return !Object.values(displayGroups).some(group => {
                    return group.categoryIds.includes(cat.id);
                  });
                }).map(cat => (
                  <div
                    key={cat.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, cat.id, null)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: theme?.colors?.border || '#ccc',
                      color: theme?.colors?.text || '#000',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      cursor: 'grab',
                      opacity: draggedCategory?.id === cat.id ? 0.5 : 1
                    }}
                  >
                    {catName(cat)}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: theme?.colors?.background || '#f5f5f5', borderRadius: '4px' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: theme?.colors?.text || '#000' }}>
                <strong>{t('note')}:</strong> {t('dragCategoriesBetweenGroups')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Group Create/Edit Form Modal */}
      {showGroupForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: theme?.colors?.text || '#000' }}>
              {editingGroup ? `${t('edit')} ${t('group')}` : `${t('new')} ${t('group')}`}
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000', fontSize: '0.9rem' }}>
                {t('key')} {!editingGroup && '*'}
              </label>
              <input
                type="text"
                value={groupFormData.group_key}
                onChange={(e) => setGroupFormData({ ...groupFormData, group_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                disabled={!!editingGroup}
                placeholder="e.g., snacks"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  backgroundColor: editingGroup ? theme?.colors?.background || '#f5f5f5' : theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  fontSize: '0.9rem'
                }}
              />
              {!editingGroup && (
                <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666', marginTop: '0.25rem' }}>
                  {t('lowercaseAlphanumericOnly')}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000', fontSize: '0.9rem' }}>
                {t('name')} *
              </label>
              <input
                type="text"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                placeholder="e.g., Snacks"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000', fontSize: '0.9rem' }}>
                {t('color')} *
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={groupFormData.color}
                  onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                  style={{
                    width: '60px',
                    height: '40px',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={groupFormData.color}
                  onChange={(e) => setGroupFormData({ ...groupFormData, color: e.target.value })}
                  placeholder="#1976d2"
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '4px',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    backgroundColor: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.text || '#000',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000', fontSize: '0.9rem' }}>
                {t('description')}
              </label>
              <input
                type="text"
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                placeholder="e.g., Chips and snacks"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={handleCancelGroupForm}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  cursor: 'pointer'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveGroupForm}
                disabled={!groupFormData.name || !groupFormData.color || (!editingGroup && !groupFormData.group_key)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: theme?.colors?.primary || '#1976d2',
                  color: '#fff',
                  cursor: (!groupFormData.name || !groupFormData.color || (!editingGroup && !groupFormData.group_key)) ? 'not-allowed' : 'pointer',
                  opacity: (!groupFormData.name || !groupFormData.color || (!editingGroup && !groupFormData.group_key)) ? 0.5 : 1
                }}
              >
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdvancedAnalytics;
